<template>
  <div :class="{ 'is-invalid': isInvalid, 'my-6': hasPadding }">
    <label v-if="label" v-html="addNbsp(label)"></label>
    <form enctype="multipart/form-data" novalidate class="mb-2">
      <div
        class="dropbox white px-11 py-7 border rounded text-center d-flex align-center justify-center"
        :class="{ 'total-size-error': totalSizeError }"
      >
        <div>
          <v-img
            max-width="24"
            max-height="24"
            contain
            :src="$asset('img/icons/upload.svg')"
            class="mb-4 mx-auto"
          />
          {{ $t('fileUpload.initial') }}
        </div>
        <input
          type="file"
          multiple
          name="file"
          class="input-file"
          :class="inputClass"
          @click="$emit('click')"
          @change="filesChange($event.target.name, $event.target.files)"
        />
      </div>
      <div v-if="totalSizeError" class="error--text text-body-small mt-1">
        {{ $t('fileUpload.totalSizeError') }}
      </div>
    </form>
    <div
      v-if="helpText"
      class="ul-attachments__help text-body-small grey-darken-1--text"
      v-html="addNbsp(helpText)"
    ></div>
    <div v-if="attachments.length" class="mt-4">
      <AttachmentRow
        :attachments="attachments"
        icon="file-uploaded"
        @clickRemove="removedAttachment => $emit('deleteAttachment', removedAttachment)"
        @attachment_downloaded="$emit('attachment_downloaded')"
      />
    </div>

    <template v-for="(attachment, index) in errorAttachments">
      <AttachmentRow
        :key="index"
        :attachments="[attachment]"
        icon="state-rejected"
        @clickRemove="removedAttachment => removeAttachmentOverSize(removedAttachment)"
      />
      <div :key="`error_${index}`" class="error--text text-body-small mb-2 mt-1">
        {{ errorMessage(attachment) }}
      </div>
    </template>
  </div>
</template>

<script>
import { v4 as uuidV4 } from 'uuid';

export default {
  name: 'FileInput',
  props: {
    label: {
      type: String,
      default: null
    },
    attachments: {
      type: Array,
      required: true
    },
    helpText: {
      type: String,
      default: null
    },
    inputClass: {
      type: String,
      required: true
    },
    allowedAttachmentTypes: {
      type: String,
      default: null
    },
    identifier: {
      type: [String, Number],
      required: false
    },
    hasPadding: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      errors: [],
      totalSizeError: false,
      errorAttachments: []
    };
  },
  computed: {
    isInvalid: {
      get() {
        return this.$store.state.forms.attachments.isInvalid;
      },
      set(value) {
        this.$store.commit('forms/attachments/setIsInvalid', value);
      }
    }
  },
  mounted() {
    this.$eventHub.$on(
      `fileInput_${this.identifier ? `${this.identifier}_` : ''}handleOutsideError`,
      this.handleFileError
    );
  },
  beforeDestroy() {
    this.$eventHub.$off(
      `fileInput_${this.identifier ? `${this.identifier}_` : ''}handleOutsideError`
    );
  },
  methods: {
    async filesChange(fieldName, fileList) {
      if (!fileList.length) {
        return;
      }

      this.totalSizeError = false;
      let allowedTypes = [];
      let validFiles = [];

      const allowedTypesImg = [
        'image/heic',
        'image/heic-sequence',
        'image/heif',
        'image/heif-sequence',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];

      const allowedTypesOthers = [
        'application/pdf',
        'audio/mp3',
        'audio/mpeg3',
        'audio/mpeg',
        'audio/x-mpeg-3',
        'video/avi',
        'video/mp4',
        'video/mpeg',
        'video/quicktime',
        'video/x-mpeg'
      ];

      if (!this.allowedAttachmentTypes) {
        allowedTypes = allowedTypesOthers.concat(allowedTypesImg);
      } else {
        allowedTypes = allowedTypesImg;
      }

      let allowedSize = 100000000;
      this.attachments.map(val => {
        allowedSize -= val.size;
      });

      for (const file of fileList) {
        allowedSize -= file.size;

        if (allowedTypes.indexOf(file.type) === -1) {
          this.handleFileError({ errorType: 'type', fileName: file.name });
          continue;
        }

        if (file.size > 20971520) {
          this.handleFileError({ errorType: 'size', fileName: file.name });
          continue;
        }

        validFiles.push(file);
      }

      if (allowedSize < 1) {
        this.handleFileError({ errorType: 'totalSize' });
        return;
      }

      for (const file of validFiles) {
        const reader = new FileReader();
        reader.onload = evt => {
          const { size, name, type } = file;
          const base64 = `data:${type};base64,${new Uint8Array(evt.target.result)}`;

          this.$emit('addAttachment', {
            uuid: uuidV4(),
            name,
            file: base64,
            fileBuffer: evt.target.result,
            mimeType: type,
            size,
            originalFile: file
          });
        };

        reader.readAsArrayBuffer(file);
      }
    },
    removeAttachmentOverSize(attachment) {
      this.errorAttachments = this.errorAttachments.filter(att => {
        if (attachment.id) {
          return att.id !== attachment.id;
        }

        return att.uuid !== attachment.uuid;
      });
    },
    handleFileError({ errorType, fileName }) {
      if (errorType === 'totalSize') {
        this.totalSizeError = true;
        return;
      }

      this.$emit('deleteAttachment', fileName);
      this.errors.push(this.$t(`fileUpload.${errorType}Error`));
      this.errorAttachments.push({
        name: fileName,
        errorType
      });
    },
    errorMessage(attachment) {
      switch (attachment.type) {
        case 'type':
          return this.$t('fileUpload.typeError');
        case 'size':
          return this.$t('fileUpload.sizeError');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#app .dropbox {
  position: relative;
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    background: rgba($primary, 0.05) !important;
    border-color: $primary !important;
  }

  .input-file {
    opacity: 0;
    width: 100%;
    height: 144px;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
  }
}

#app .is-invalid {
  .dropbox {
    background: rgba($error, 0.05) !important;
    border-color: $error !important;

    &:hover {
      background: rgba($error, 0.05) !important;
    }
  }
}
</style>
